import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { unwrapResult } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import { DispatchProperties } from '../../redux/store';
import {
	getBehaviorDurationGraph,
	getBehaviorFrequencyGraph,
	getDTTGraph,
	getTAGraph,
} from '../../redux/State/clientSlice/graphSlice';
import {
	getBehaviorReductionAllAnnotations,
	getTargetAllAnnotations,
} from '../../redux/State/clientSlice/graphAnnotationSlice';
import { TargetTypes } from '../../redux/API/ClientAPIHelpers/programTargetsProperties';
import { DisplayDataProperties } from './types';
import { GRAPH_CONSTANTS } from './constants';
import { errorNormalizer } from '../../shared/Helpers/functions';
import { filterAnnotationsByDate } from './utils';

type GraphType = 'target' | 'behavior';
type TargetSubType = TargetTypes.DTT | TargetTypes.TA;
type BehaviorSubType = 'Frequency' | 'Duration';

interface UseGraphDataProperties {
	endDate: string;
	graphType: GraphType;
	id: string;
	periodicity: string;
	startDate: string;
	subType: TargetSubType | BehaviorSubType;
}

export const useGraphData = ({ id, startDate, endDate, periodicity, graphType, subType }: UseGraphDataProperties) => {
	const dispatch = useDispatch<DispatchProperties>();
	const [displayData, setDisplayData] = useState<DisplayDataProperties[]>([]);

	useEffect(() => {
		if (!id) return;

		const fetchData = async () => {
			try {
				let response;

				if (graphType === 'target') {
					const action = subType === TargetTypes.DTT ? getDTTGraph : getTAGraph;
					response = await dispatch(
						action({
							targetId: id,
							startDate,
							endDate,
							periodicity,
						}),
					).then(unwrapResult);

					const data = response.map((item) => ({
						x: dayjs(item.date).format(GRAPH_CONSTANTS.DATETIME_FORMAT),
						y: item.percentage,
					}));
					setDisplayData(data);
				} else if (subType === 'Frequency') {
					response = await dispatch(
						getBehaviorFrequencyGraph({
							behaviorId: id,
							startDate,
							endDate,
							periodicity,
						}),
					).then(unwrapResult);

					const data = response.map((item) => ({
						x: dayjs(item.date).format(GRAPH_CONSTANTS.DATETIME_FORMAT),
						y: item.count,
					}));
					setDisplayData(data);
				} else {
					response = await dispatch(
						getBehaviorDurationGraph({
							behaviorId: id,
							startDate,
							endDate,
							periodicity,
						}),
					).then(unwrapResult);

					const data = response.map((item) => ({
						x: dayjs(item.date).format(GRAPH_CONSTANTS.DATETIME_FORMAT),
						y: item.duration / 60,
					}));
					setDisplayData(data);
				}
			} catch (error: any) {
				errorNormalizer(error);
			}
		};

		fetchData();
	}, [id, startDate, endDate, periodicity, graphType, subType, dispatch]);

	return { displayData };
};

interface UseGraphAnnotationsProperties {
	annotationsList: any[];
	graphType: GraphType;
	id: string;
	startDate: string;
}

export const useGraphAnnotations = ({ id, graphType, annotationsList, startDate }: UseGraphAnnotationsProperties) => {
	const dispatch = useDispatch<DispatchProperties>();

	useEffect(() => {
		if (!id) return;

		if (graphType === 'target') {
			dispatch(getTargetAllAnnotations(id)).then(unwrapResult).catch(errorNormalizer);
		} else {
			dispatch(getBehaviorReductionAllAnnotations(id)).then(unwrapResult).catch(errorNormalizer);
		}
	}, [id, graphType, dispatch]);

	const chartAnnotations = useMemo(() => {
		if (!annotationsList) return [];

		const filtered = filterAnnotationsByDate(annotationsList, startDate);

		return filtered.map((annotation) => ({
			type: 'line',
			borderColor: GRAPH_CONSTANTS.ANNOTATION_LINE_COLOR,
			borderWidth: GRAPH_CONSTANTS.ANNOTATION_LINE_WIDTH,
			scaleID: 'x',
			id: annotation.id,
			value: new Date(annotation.date),
		}));
	}, [annotationsList, startDate]);

	const filteredAnnotations = useMemo(
		() => filterAnnotationsByDate(annotationsList, startDate),
		[annotationsList, startDate],
	);

	return { chartAnnotations, filteredAnnotations };
};

interface UseChartOptionsProperties {
	annotations: any[];
	title: string;
}

export const useChartOptions = ({ title, annotations }: UseChartOptionsProperties) =>
	useMemo(
		() => ({
			plugins: {
				legend: {
					position: 'top' as const,
				},
				title: {
					display: true,
					text: title,
				},
				annotation: {
					annotations,
				},
			},
			backgroundColor: 'white',
		}),
		[title, annotations],
	);
